import { showUserAlert } from "../utils/userErrorMessage";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import { useNavigate } from "react-router-dom";

export default function UserManagement() {
  const navigate = useNavigate();
  const [role, setRole] = useState("light");
  const [owner, setOwner] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const email = localStorage.getItem("email") || "";

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data: prof, error: profErr } = await supabase
        .from("profiles")
        .select("id, email, role, driver_name")
        .eq("email", email)
        .maybeSingle();

      if (profErr) throw profErr;

      if (prof) {
        const { data: profileExtra } = await supabase
          .from("profiles")
          .select("hire_date, position, branch, user_id")
          .eq("id", prof.id)
          .maybeSingle();

        setOwner({ ...prof, ...(profileExtra || {}) });
        setRole(prof.role || "light");
      } else {
        setOwner({ email, role: "light", driver_name: "", position: "", branch: "", user_id: "" });
        setRole("light");
      }

      const { data: cl, error: clErr } = await supabase
        .from("client")
        .select("*")
        .eq("company", email)
        .order("id", { ascending: true });

      if (clErr) throw clErr;
      setClients(cl || []);
    } catch (e) {
      console.error("사용자 목록 로드 실패:", e.message);
      setOwner({ email, role: "light", driver_name: "", position: "", branch: "", user_id: "" });
      setClients([]);
      setRole("light");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const ownerRow = owner
      ? {
          __source: "profiles",
          id: owner.id || "",
          name: owner.driver_name || "-",
          hire_date: owner.hire_date || "",
          position: owner.position || "-",
          branch: owner.branch || "-",
          email: owner.email || "-",
        }
      : null;

    const clientRows = clients.map((c) => ({
      __source: "client",
      id: c.id,
      name: c.name || "-",
      hire_date: c.hire_date || "",
      position: c.position || "-",
      branch: c.branch || "-",
      email: c.email || "-",
    }));

    if (role === "light") {
      return ownerRow ? [ownerRow] : [];
    }

    return ownerRow ? [ownerRow, ...clientRows] : clientRows;
  }, [owner, clients, role]);

  const isFirstLevelRole = role === "first" || role === "operator";
  const canAdd = isFirstLevelRole && rows.length < 10;

  const goEdit = (user) => {
    if (!user.id) {
      showUserAlert("수정할 사용자 정보를 찾을 수 없습니다.");
      return;
    }
    navigate(`/user-edit/${user.__source}/${encodeURIComponent(user.id)}`);
  };

  return (
    <div className="space-y-1">
      <style>{`
        .user-management-table th,
        .user-management-table td {
          text-align: center;
        }

        .user-management-table .col-seq {
          width: 40px;
          min-width: 40px;
          white-space: nowrap;
        }

        .user-management-table .col-email {
          width: 220px;
          min-width: 220px;
        }
      `}</style>
      <Header />
      <div className="px-4 pt-1 pb-4">
        <h2 className="text-lg font-semibold">사용자 관리</h2>

        <Card>
          <CardContent className="bg-white">
            <div className="overflow-x-auto">
              <table className="user-management-table table-auto w-full border border-gray-300 text-sm">
                <thead className="bg-blue-500 text-white">
                  <tr>
                    {["순번", "이름", "입사일", "직위", "지점명", "Email"].map((h) => (
                      <th
                        key={h}
                        className={
                          "border border-gray-300 p-2 " +
                          (h === "순번" ? "col-seq " : "") +
                          (h === "Email" ? "col-email " : "")
                        }
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td className="border p-2 text-center" colSpan={6}>
                        데이터가 없습니다.
                      </td>
                    </tr>
                  )}
                  {rows.map((u, idx) => (
                    <tr key={`${u.__source}-${u.id || idx}`}>
                      <td className="border border-gray-300 p-2 col-seq">
                        <button
                          type="button"
                          className="text-blue-700 underline underline-offset-2"
                          onClick={() => goEdit(u)}
                        >
                          {idx + 1}
                        </button>
                      </td>
                      <td className="border border-gray-300 p-2">{u.name}</td>
                      <td className="border border-gray-300 p-2">{(u.hire_date || "").slice(0, 10)}</td>
                      <td className="border border-gray-300 p-2">{u.position}</td>
                      <td className="border border-gray-300 p-2">{u.branch}</td>
                      <td className="border border-gray-300 p-2 col-email">{u.email}</td>
                    </tr>
                  ))}
                  {loading && (
                    <tr>
                      <td className="border p-2 text-center" colSpan={6}>
                        불러오는 중...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {isFirstLevelRole && (
          <div className="flex items-center justify-between mt-3">
            <div className="text-sm text-gray-500">{`총원: ${rows.length} / 10`}</div>
            <Button
              onClick={() => {
                if (!canAdd) {
                  showUserAlert("등록 가능한 사용자 수는 최대 10명입니다.");
                  return;
                }
                navigate("/user-add");
              }}
              className="mt-2"
              disabled={loading}
            >
              추가 등록
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
